import { Check, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'

export type ReimbursementDocumentUploadStatusKey =
  | 'pending'
  | 'uploaded'
  | 'approved'
  | 'incomplete'
  | 'unknown'

const BADGE_BASE =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap'

export function normalizeReimbursementUploadStatusKey(
  status: string,
): ReimbursementDocumentUploadStatusKey {
  const normalized = String(status ?? '').trim().toLowerCase()
  if (normalized === 'pending') return 'pending'
  if (normalized === 'uploaded') return 'uploaded'
  if (
    normalized === 'approved' ||
    normalized === 'received' ||
    normalized === 'complete' ||
    normalized === 'completed'
  ) {
    return 'approved'
  }
  if (
    normalized === 'incomplete' ||
    normalized === 'rejected' ||
    normalized === 'missing' ||
    normalized === 'denied'
  ) {
    return 'incomplete'
  }
  return 'unknown'
}

export function normalizeReimbursementUploadStatusLabel(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return 'Pending'
    case 'uploaded':
      return 'Uploaded'
    case 'approved':
      return 'Approved'
    case 'incomplete':
      return 'Incomplete'
    default:
      return String(status ?? '').trim() || 'Pending'
  }
}

export function getReimbursementDocumentUploadStatusBadgeClasses(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return `${BADGE_BASE} bg-gray-100 text-gray-700`
    case 'uploaded':
      return `${BADGE_BASE} bg-amber-100 text-amber-700`
    case 'approved':
      return `${BADGE_BASE} bg-green-100 text-green-700`
    case 'incomplete':
      return `${BADGE_BASE} bg-red-100 text-red-700`
    default:
      return `${BADGE_BASE} bg-gray-100 text-gray-700`
  }
}

export function getReimbursementDocumentUploadStatusTopBarClass(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return 'border-t-gray-300'
    case 'uploaded':
      return 'border-t-amber-400'
    case 'approved':
      return 'border-t-green-500'
    case 'incomplete':
      return 'border-t-red-500'
    default:
      return 'border-t-slate-200'
  }
}

export function reimbursementDocumentUploadStatusShowsCheckIcon(status: string): boolean {
  return normalizeReimbursementUploadStatusKey(status) === 'approved'
}

export function reimbursementDocumentUploadStatusShowsXCircleIcon(status: string): boolean {
  return normalizeReimbursementUploadStatusKey(status) === 'incomplete'
}

export interface ReimbursementDocumentUploadStatusBadgeProps {
  status: string
  className?: string
}

export function ReimbursementDocumentUploadStatusBadge({
  status,
  className,
}: ReimbursementDocumentUploadStatusBadgeProps) {
  const label = normalizeReimbursementUploadStatusLabel(status)
  const showCheck = reimbursementDocumentUploadStatusShowsCheckIcon(status)
  const showXCircle = reimbursementDocumentUploadStatusShowsXCircleIcon(status)

  return (
    <span className={cn(getReimbursementDocumentUploadStatusBadgeClasses(status), className)}>
      {showCheck ? <Check className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {showXCircle ? <XCircle className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {label}
    </span>
  )
}
